'use client';

import { useRef, useState, useEffect, useCallback } from 'react';

import { useUser } from 'src/hooks/use-user';

import { isOtherModalOpen, useServicePopupHeld } from './service-popup-hold';
import { SERVICE_POPUP_DWELL_MS, SERVICE_POPUP_RETRY_MS } from './service-popup-constants';
import { toPathPopupFacts, pickServicePopup, isServicePopupPath, toServicePopupCopy } from './service-popup';

import type { ServicePopup, ServicePopupShown } from './service-popup';

/** 팝업을 고르는 재료 — `useServicePopupFacts` */
export type ServicePopupFacts = {
  ready: boolean;
  trial: { daysLeft: number; endMs: number } | null;
  bonusMonths: number;
  setup: boolean;
  brokenAccounts: readonly string[];
};

/**
 * **서비스 팝업 한 장** — 서비스에 머문 지 30초가 지나면 한 번 고르고(`pickServicePopup`), 띄운 때를 이 브라우저에 남긴다.
 *
 * - 30초는 서비스 틀이 선 때부터 잰다 — 화면을 옮겨도 이어서 센다. 하려던 것을 먼저 보게 둔다.
 * - 30초가 지났어도 기다리는 때 — 붙든 화면이 있다(`useServicePopupHold` — 대화 화면의 답이 서는 중) · 다른 창이 떠 있다(`isOtherModalOpen` · 잠깐씩 다시 본다) · 서비스 화면 밖이다.
 * - 그 일을 하는 화면에서는 그 장을 빼고 고른다(`toPathPopupFacts`) — 뺀 장만 해당하면 고르지 않고 다른 화면으로 옮길 때 다시 본다.
 * - 마지막으로 띄운 때는 사람 · 워크스페이스마다(`service-popup:{uid}:{workspaceId}`) — 다른 기기는 따로 잰다.
 * - 같은 방문(탭 세션)에서 한 장을 닫았으면 더 띄우지 않는다(`service-popup:closed`).
 * - 틀이 서 있는 동안 고르는 일은 한 번이다 — 그 뒤 재료가 바뀌어도 다시 고르지 않는다.
 * - 저장은 편의다 — 저장소가 막혀도(사생활 창) 화면은 선다(그때는 들어올 때마다 고른다).
 * @param facts 고르는 재료
 * @param workspaceId 지금 워크스페이스
 * @param pathname 지금 주소의 경로
 */
export function useServicePopup({ facts, workspaceId, pathname }: { facts: ServicePopupFacts; workspaceId: string; pathname: string | null | undefined }) {
  const user = useUser();
  const uid = String(user?.uid ?? '').trim();
  const [popup, setPopup] = useState<ServicePopup | null>(null);
  const decided = useRef(false);
  const [dwelt, setDwelt] = useState(false);
  const held = useServicePopupHeld();
  /** 다른 창이 떠 있어 미룬 뒤 다시 볼 때마다 오른다 */
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDwelt(true), SERVICE_POPUP_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (decided.current || !dwelt || held || !isServicePopupPath(pathname) || !facts.ready || !uid || !workspaceId) return undefined;
    if (isOtherModalOpen()) {
      const timer = window.setTimeout(() => setRetry((n) => n + 1), SERVICE_POPUP_RETRY_MS);
      return () => window.clearTimeout(timer);
    }
    if (readVisitClosed()) {
      decided.current = true;
      return undefined;
    }
    const key = `${KEY_PREFIX}${uid}:${workspaceId}`;
    const lastShown = readShown(key);
    const now = Date.now();
    const next = pickServicePopup({ ...toPathPopupFacts(facts, pathname), lastShown, now });
    if (!next) {
      // 이 화면이 뺀 장이 해당하면 다른 화면에서 다시 본다
      if (!pickServicePopup({ ...facts, lastShown, now })) decided.current = true;
      return undefined;
    }
    decided.current = true;
    writeShown(key, { ...lastShown, [next.kind]: now });
    setPopup(next);
    return undefined;
  }, [dwelt, held, pathname, facts, uid, workspaceId, retry]);

  /** 닫기 — 바깥 · Esc · 단추 모두 이 길이다 */
  const close = useCallback(() => {
    markVisitClosed();
    setPopup(null);
  }, []);

  return { popup, copy: popup ? toServicePopupCopy(popup) : null, close };
}

// ---- 이하 비공개 ----------------------------------------------------------

const KEY_PREFIX = 'service-popup:';
const CLOSED_KEY = 'service-popup:closed';

/** 갈래마다 마지막으로 띄운 때 — 없거나 못 읽으면 빈 값 */
function readShown(key: string): ServicePopupShown {
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) ?? '{}') as Record<string, unknown>;
    return Object.fromEntries(Object.entries(raw).filter(([, at]) => typeof at === 'number' && Number.isFinite(at))) as ServicePopupShown;
  } catch {
    return {};
  }
}

function writeShown(key: string, shown: ServicePopupShown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(shown));
  } catch {
    /* 편의 저장 — 막혀도 화면은 선다 */
  }
}

function readVisitClosed(): boolean {
  try {
    return window.sessionStorage.getItem(CLOSED_KEY) === '1';
  } catch {
    return false;
  }
}

function markVisitClosed(): void {
  try {
    window.sessionStorage.setItem(CLOSED_KEY, '1');
  } catch {
    /* 편의 저장 */
  }
}
